/*
 * Copyright (c) 2021-2025.  atagon GmbH.  All rights reserved.
 *
 * This file is part of the atagon codebase and may not be copied,
 *  distributed, or modified without explicit written permission from
 * atagon GmbH. https://www.atagon.com
 */

import React from 'react';
import PropTypes from "prop-types";
import DisplayIcon from "../Icons/DisplayIcon.jsx";
import TextTruncate from "../TextTruncate.jsx";
import {GlobaleStyles} from "../../Constants.js";


function TextInput({
                       leadingIconName,
                       leadingIconClassName = "",
                       leadingImageURL,
                       type = "text",
                       label,
                       value,
                       hasSearch,
                       isReadOnly,
                       placeholder = '---',
                       onChange,
                       onFocus,
                       onBlur,
                       onKeyDown,
                       onClick,
                       onFocusCapture,
                       onPaste,
                       onMouseOver,
                       id,
                       className,
                       style,
                       innerRef,
                       suggestion,
                       hasDot,
                       dotColor = '#a7a7a7',
                       dotAnimated,
                       hasArrow,
                       arrowColor,
                       arrowAnimated,
                       hasClose,
                       closeColor,
                       closeAnimated,
                       onCloseClick,
                       extend,
                       searchColor,
                       searchAnimated,
                       hasIcon,
                       iconName,
                       iconColor,
                       iconAnimated,
                       onArrowClick
                   }) {
    let hasLeadingElement = leadingIconName || leadingImageURL || hasSearch
    let hasTrailingElement = hasArrow

    return (<>
        {(label !== undefined) && (
            <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>)}
        <div className=" relative  group w-full" style={style}>
            {leadingIconName && (<>
                <div
                    className={`pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3 `}>
                    <DisplayIcon iconName={leadingIconName}
                                 className={`h-4 w-4 text-stone-400 ${leadingIconClassName}`}/>
                </div>
            </>)}

            {leadingImageURL && (<>
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <img src={leadingImageURL} alt={"image"} className={"object-contain w-5 h-5"}/>

                </div>
            </>)}

            {hasSearch && (<>
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3">
                    <DisplayIcon iconName={"IoSearchOutline"} className=" h-4 w-4 text-stone-600 bg-white"
                                 style={{color: searchColor}}/>
                </div>
            </>)}

            {isReadOnly ? (<div
                className={`px-3 flex items-center  sm:text-sm rounded-xs shadow-xs h-9.5 w-full ${GlobaleStyles.isReadOnly} ${hasTrailingElement && "pr-8"} ${hasLeadingElement && ("pl-10")}`}
                ref={innerRef}
            >
                <TextTruncate>{(value ?? placeholder).toString()}</TextTruncate>
            </div>) : (<input type={type} style={{caretColor: "rgba(96, 165, 250, 100)"}}
                              autoComplete={"off"}
                              ref={innerRef}
                              value={value ?? ""}
                              onChange={(event) => {
                                  onChange?.(event.target.value, event)
                              }}
                              onFocus={onFocus}
                              onBlur={onBlur}
                              onKeyDown={onKeyDown}
                              onClick={onClick}
                              onFocusCapture={onFocusCapture}
                              onPaste={onPaste}
                              onMouseOver={onMouseOver}
                              id={id}
                              className={`rounded-md shadow-xs focus:ring-blue-300   focus:border-blue-300 hover:border-gray-400 focus:ring-inset block w-full sm:text-sm border-gray-300 bg-gray-50/25 rounded-xs  placeholder:text-slate-400 placeholder-black/20 font-normal transition-[padding] transition-[box-shadow] duration-200 ${hasTrailingElement && "pr-8!"} ${hasLeadingElement && ("pl-10!")} ${className} `}
                              placeholder={placeholder}/>)}

            <div className="absolute inset-y-0 right-0  space-x-3 flex items-center pointer-events-none">
                {/*right textbox extension*/}
                {hasDot && (<>
                            <span className="relative inline-flex rounded-full h-2 w-2"
                                  style={{background: dotColor}}/>
                    {dotAnimated && (<span
                        className="animate-ping absolute inline-flex h-2 w-2 rounded-full bg-purple-400 opacity-75"/>)}
                </>)}

                {hasArrow && (<>
                    <DisplayIcon
                        iconName={"IoChevronDownSharp"}
                        className="relative inline-flex rounded-full h-4 w-4 cursor-pointer pointer-events-auto"
                        onClick={(event) => {
                            onClick?.(event)
                            onArrowClick?.()
                        }}
                        style={{color: arrowColor}}/>
                    {arrowAnimated && (
                        <span className="animate-ping absolute inline-flex h-3 w-3 rounded-full  opacity-30 "
                              style={{background: arrowColor}}/>)}
                </>)}


                {hasIcon && (<>
                    <DisplayIcon iconName={iconName}
                                 className={"relative inline-flex rounded-full h-4 w-4"}
                                 color={iconColor}/>
                    {iconAnimated && (
                        <span className="animate-ping absolute inline-flex h-4 w-4 rounded-full  opacity-30 "
                              style={{background: iconColor}}/>)}
                </>)}

                {hasClose && (<>
                    <DisplayIcon
                        iconName={"IoCloseSharp"}
                        className="relative inline-flex rounded-full h-4 w-4 cursor-pointer pointer-events-auto"
                        onClick={() => {
                            onCloseClick?.()
                        }}
                        style={{color: closeColor}}/>
                    {closeAnimated && (
                        <span className="animate-ping absolute inline-flex h-4 w-4 rounded-full  opacity-30 "
                              style={{background: closeColor}}/>)}
                </>)}

                <span className={'pointer-events-auto'}>
                        {extend}
                        </span>

            </div>

        </div>
    </>);
}

TextInput.propTypes = {
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    label: PropTypes.string,
    suggestion: PropTypes.string,
    placeholder: PropTypes.string,
    isReadOnly: PropTypes.bool,
    onChange: PropTypes.func,
    onFocus: PropTypes.func,
    onBlur: PropTypes.func,
    onKeyDown: PropTypes.func,
    onClick: PropTypes.func,
    onFocusCapture: PropTypes.func,
    innerRef: PropTypes.any,
    hasDot: PropTypes.bool,
    dotColor: PropTypes.string,
    dotAnimated: PropTypes.bool,
    hasArrow: PropTypes.bool,
    arrowColor: PropTypes.string,
    arrowAnimated: PropTypes.bool,
    hasClose: PropTypes.bool,
    closeColor: PropTypes.string,
    closeAnimated: PropTypes.bool,
    onCloseClick: PropTypes.func,
    onMouseOver: PropTypes.func,
    extend: PropTypes.element,
    hasSearch: PropTypes.bool,
    searchColor: PropTypes.string,
    searchAnimated: PropTypes.bool,
    hasIcon: PropTypes.bool,
    iconName: PropTypes.string,
    iconColor: PropTypes.string,
    iconAnimated: PropTypes.bool,
};
TextInput.defaultProps = {
    isReadOnly: false, placeholder: '---', hasDot: false, hasIcon: false, dotColor: '#a7a7a7', dotAnimated: false,
}

export default TextInput;
